import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  Link,
} from '@mui/material';
import { Warning, Refresh, Payment, Visibility } from '@mui/icons-material';
import { apiService } from '../services/api';
import { useNavigate } from 'react-router-dom';

interface PaymentRequiringAttention {
  paymentId: string;
  jobId: string;
  customerId: string;
  providerId: string;
  customerName: string;
  providerName: string;
  amount: number;
  failureReason: string | null;
  retryAttempts: number;
  firstFailedAt: string | null;
  serviceType: string | null;
  address: string | null;
}

const formatDate = (d: string | null) => {
  if (!d) return '—';
  try {
    const x = new Date(d);
    return x.toLocaleDateString(undefined, { dateStyle: 'medium' });
  } catch {
    return '—';
  }
};

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const AdminPaymentsRequiringAttentionPage: React.FC = () => {
  const navigate = useNavigate();
  const [list, setList] = useState<PaymentRequiringAttention[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);

  const fetchList = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.get<PaymentRequiringAttention[]>('/admin/payments-requiring-attention');
      const data = Array.isArray((res as any).data) ? (res as any).data : [];
      setList(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load payments');
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const handleRetry = async (jobId: string) => {
    if (!window.confirm('Retry payment for this job?')) return;
    setRetryingId(jobId);
    try {
      const res = await apiService.post(`/payments/retry-payment/${jobId}`, {}, { timeout: 60000 }) as any;
      if (res?.success) {
        const status = res?.payment?.status;
        if (status === 'completed') {
          alert('Payment processed successfully.');
        } else if (status === 'failed') {
          alert(`Payment retry failed: ${res?.payment?.failureReason || res?.error || 'Unknown error'}`);
        } else {
          alert(`Payment status: ${status || 'pending'}. Check provider dashboard or refresh.`);
        }
        await fetchList();
      } else {
        alert(res?.error || res?.message || 'Retry failed');
      }
    } catch (err: any) {
      alert(err?.message || err?.error || 'Failed to retry payment');
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Warning sx={{ fontSize: 32, color: 'warning.main' }} />
          <Typography variant="h5" component="h1">
            Payments Requiring Attention
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchList}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Failed payments that have exceeded automatic retries. Retry payment after the customer updates their payment method, or use Good Faith Payment for manual resolution.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : list.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No payments requiring attention.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Job ID</strong></TableCell>
                <TableCell><strong>Customer</strong></TableCell>
                <TableCell><strong>Provider</strong></TableCell>
                <TableCell><strong>Service</strong></TableCell>
                <TableCell><strong>Address</strong></TableCell>
                <TableCell><strong>Amount</strong></TableCell>
                <TableCell><strong>Failure reason</strong></TableCell>
                <TableCell><strong>Retries</strong></TableCell>
                <TableCell><strong>First failed</strong></TableCell>
                <TableCell align="right"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((row) => (
                <TableRow key={row.paymentId} hover>
                  <TableCell>
                    <Link
                      component="button"
                      variant="body2"
                      onClick={() => navigate(`/job/${row.jobId}`)}
                      sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                    >
                      {row.jobId.slice(-8)}
                    </Link>
                  </TableCell>
                  <TableCell>{row.customerName}</TableCell>
                  <TableCell>{row.providerName}</TableCell>
                  <TableCell>{row.serviceType || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 180 }} title={row.address || ''}>
                    <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.address || '—'}
                    </Box>
                  </TableCell>
                  <TableCell>{formatCurrency(row.amount)}</TableCell>
                  <TableCell sx={{ maxWidth: 200 }} title={row.failureReason || ''}>
                    <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.failureReason || '—'}
                    </Box>
                  </TableCell>
                  <TableCell>{row.retryAttempts}</TableCell>
                  <TableCell>{formatDate(row.firstFailedAt)}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Visibility />}
                      onClick={() => navigate(`/job/${row.jobId}`)}
                      sx={{ mr: 1 }}
                    >
                      View
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      startIcon={retryingId === row.jobId ? <CircularProgress size={16} /> : <Payment />}
                      disabled={retryingId !== null}
                      onClick={() => handleRetry(row.jobId)}
                    >
                      {retryingId === row.jobId ? 'Processing…' : 'Retry Payment'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default AdminPaymentsRequiringAttentionPage;
